import { MAX_RECOMMENDED_EXERCISES } from './coach.js';
/** Local hour of the exercise reminder when the user has not picked one. */
export const DEFAULT_EXERCISE_REMINDER_HOUR = 15;
/** Every day, until the user picks days (0 = Sunday). */
export const DEFAULT_EXERCISE_REMINDER_DAYS = [0, 1, 2, 3, 4, 5, 6];
/** The session reminder's weekdays, reading settings saved before multi-day. */
export function sessionReminderDays(settings) {
    return settings.reminderDays ?? [settings.reminderDow];
}
/**
 * Is the session reminder due? On, at its hour, on one of its weekdays — and
 * not if a session was already recorded today (local), which replaced the
 * old every-3/7-days rule: the reminder exists to get today's session done.
 */
export function sessionReminderDue(settings, localHour, localDayOfWeek, sessionAlreadyToday) {
    if (settings.sessionReminder === false)
        return false;
    if (localHour !== settings.reminderHour)
        return false;
    if (!sessionReminderDays(settings).includes(localDayOfWeek))
        return false;
    return !sessionAlreadyToday;
}
/** Is the exercise reminder due at this local hour and weekday? */
/**
 * Whether the exercise reminder is on. An explicit choice in Settings wins;
 * until there is one, it switches on by itself once the user has recorded a
 * session — before that there are no picks to remind them of.
 */
export function exerciseReminderOn(settings, hasSession) {
    return settings.exerciseReminder ?? hasSession;
}
export function exerciseReminderDue(settings, localHour, localDayOfWeek, hasSession = false) {
    if (!exerciseReminderOn(settings, hasSession))
        return false;
    if (localHour !== (settings.exerciseReminderHour ?? DEFAULT_EXERCISE_REMINDER_HOUR))
        return false;
    return (settings.exerciseReminderDays ?? DEFAULT_EXERCISE_REMINDER_DAYS).includes(localDayOfWeek);
}
const WINDOW_DAYS = 28;
const DAY_MS = 24 * 60 * 60 * 1000;
const TAGS_FOR = {
    forwardHead: ['fhp'],
    slouch: ['thoracic-ext', 'scap'],
    recline: ['core', 'lbp'],
    balance: ['asymmetry'],
};
/** What each focus means, in the coach's words. Wellness wording (LEGAL §4). */
export const PLAN_FOCUS_COPY = {
    forwardHead: 'Your head drifted forward more than anything else.',
    slouch: 'Your upper back rounded the most.',
    recline: 'You spent the most time leaning back.',
    balance: 'One side at a time — desk work tends to favour one side.',
};
export function isoWeekIndex(ms) {
    // Weeks since a fixed Monday; only used to rotate, so any stable origin works.
    return Math.floor((ms - Date.UTC(2024, 0, 1)) / (7 * DAY_MS));
}
export function weeklyPlan(sessions, library, now = Date.now()) {
    const recent = sessions.filter((s) => now - s.startedAt <= WINDOW_DAYS * DAY_MS);
    if (recent.length === 0)
        return { picks: [], topFocus: null, sessionCount: 0 };
    const minutes = [
        ['forwardHead', recent.reduce((sum, s) => sum + s.fhpMin, 0)],
        ['slouch', recent.reduce((sum, s) => sum + s.slouchMin, 0)],
        ['recline', recent.reduce((sum, s) => sum + s.reclineMin, 0)],
    ]
        .filter(([, m]) => m > 0)
        .sort((a, b) => b[1] - a[1]);
    const focuses = minutes.slice(0, 2).map(([f]) => f);
    // Nothing went off-posture: keep the habit with the two most common areas.
    if (focuses.length === 0)
        focuses.push('forwardHead', 'slouch');
    else if (focuses.length === 1)
        focuses.push(focuses[0]);
    focuses.push('balance');
    const week = isoWeekIndex(now);
    const used = new Set();
    const picks = [];
    for (const [slot, focus] of focuses.entries()) {
        const pool = library
            .filter((e) => e.difficulty <= 2 && e.tags.some((t) => TAGS_FOR[focus].includes(t)))
            .filter((e) => !used.has(e.id))
            .sort((a, b) => a.difficulty - b.difficulty || a.id.localeCompare(b.id));
        if (pool.length === 0)
            continue;
        // Offset by slot so a repeated focus does not land on the same rotation step.
        const pick = pool[(week + slot) % pool.length];
        used.add(pick.id);
        picks.push({ exerciseId: pick.id, focus });
        if (picks.length === MAX_RECOMMENDED_EXERCISES)
            break;
    }
    return { picks, topFocus: minutes[0]?.[0] ?? null, sessionCount: recent.length };
}
/**
 * The picks Darwin stands behind right now: this four-week plan, or — when the
 * last session is older than that — the plan as of the latest session, so
 * history still says what to work on. Empty only with no sessions at all.
 * The Progress card, the exercise reminder and the rest-break banner all use
 * this, so they always name the same one to three movements.
 */
export function currentPicks(sessions, exercises, now = Date.now()) {
    const plan = weeklyPlan(sessions, exercises, now);
    if (plan.picks.length > 0 || sessions.length === 0)
        return plan;
    const latest = Math.max(...sessions.map((s) => s.startedAt));
    return weeklyPlan(sessions, exercises, latest);
}
//# sourceMappingURL=plan.js.map